import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/constants/routes';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Modal, ModalHeader, ModalTitle, ModalBody } from '@/components/ui/modal';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { useUnblockRequests, useProcessUnblockRequest } from '../hooks/useFraud';
import type { UserBlock } from '../api/fraud.api';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All Requests' },
];

const unblockRequestStatusVariant = {
  pending: 'warning' as const,
  approved: 'success' as const,
  rejected: 'destructive' as const,
  none: 'secondary' as const,
};

function ProcessDialog({
  block,
  action,
  onClose,
}: {
  block: UserBlock;
  action: 'approved' | 'rejected';
  onClose: () => void;
}) {
  const [notes, setNotes] = useState('');
  const { mutate: process, isPending } = useProcessUnblockRequest();

  const handleSubmit = () => {
    process(
      { blockId: block.id, payload: { status: action, admin_notes: notes.trim() || undefined } },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal isOpen onClose={onClose}>
      <ModalHeader onClose={onClose}>
        <ModalTitle>{action === 'approved' ? 'Approve' : 'Reject'} Unblock Request</ModalTitle>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
          <p><span className="text-muted-foreground">Customer:</span> {block.customer?.name ?? block.customer_id.slice(0, 8)}</p>
          <p><span className="text-muted-foreground">Request reason:</span> {block.unblock_request_reason}</p>
          <p><span className="text-muted-foreground">Blocked until:</span> {formatDate(block.blocked_until)}</p>
        </div>

        <div className="space-y-2">
          <Label>Admin Notes (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a note visible to the team..."
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            variant={action === 'approved' ? 'success' : 'destructive'}
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? 'Processing...' : action === 'approved' ? 'Approve & Unblock' : 'Reject Request'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

export function UnblockRequestsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [page, setPage] = useState(1);
  const [processing, setProcessing] = useState<{ block: UserBlock; action: 'approved' | 'rejected' } | null>(null);

  const { data, isLoading } = useUnblockRequests({ page, limit: 20, status: status === 'all' ? undefined : status });

  const blocks = data?.blocks ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Unblock Requests"
        description="Review customer requests to lift temporary bans"
        breadcrumbs={[{ label: 'Fraud & Safety', href: ROUTES.FRAUD }, { label: 'Unblock Requests' }]}
        actions={
          <Button variant="outline" onClick={() => navigate(ROUTES.FRAUD_REPORTS)}>
            View Fraud Reports
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <Select value={status} onChange={(v) => { setStatus(v as any); setPage(1); }} options={statusOptions} className="w-40" />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : blocks.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon="default"
                title="No unblock requests"
                description="No requests match the selected filter."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Block Reason</TableHead>
                  <TableHead>Request Reason</TableHead>
                  <TableHead>Blocked Until</TableHead>
                  <TableHead>Requested On</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blocks.map((block) => (
                  <TableRow key={block.id}>
                    <TableCell>
                      <button
                        className="text-sm font-medium hover:underline text-left"
                        onClick={() => navigate(buildRoute(ROUTES.CUSTOMER_DETAIL, { id: block.customer_id }))}
                      >
                        {block.customer?.name ?? block.customer_id.slice(0, 8)}
                      </button>
                      {block.customer?.phone && (
                        <p className="text-xs text-muted-foreground">{block.customer.phone}</p>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[160px]">
                      <p className="text-sm truncate">{block.reason}</p>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <p className="text-sm truncate">{block.unblock_request_reason ?? '—'}</p>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDate(block.blocked_until)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {block.unblock_request_at ? formatDate(block.unblock_request_at) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={unblockRequestStatusVariant[block.unblock_request_status] ?? 'secondary'} className="capitalize">
                        {block.unblock_request_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {block.unblock_request_status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-success border-success/40 hover:bg-success/10"
                            onClick={() => setProcessing({ block, action: 'approved' })}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={() => setProcessing({ block, action: 'rejected' })}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                      {block.admin_notes && block.unblock_request_status !== 'pending' && (
                        <p className="text-xs text-muted-foreground max-w-[120px] truncate" title={block.admin_notes}>
                          {block.admin_notes}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Next
          </Button>
        </div>
      )}

      {/* Process Dialog */}
      {processing && (
        <ProcessDialog
          block={processing.block}
          action={processing.action}
          onClose={() => setProcessing(null)}
        />
      )}
    </div>
  );
}
